/* eslint-disable import/no-duplicates */
import { Button, Title, Icon, AppointmentListItem, message } from 'components';
import { Text } from 'components/Typography/Text';
import { isAfter } from 'date-fns';
import { isBefore } from 'date-fns/esm';
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { createFormatDateFunction, getLocalTimeZone } from 'utils';
import errorReporter from '@app/errorReporter';
import { AppointmentWithThumbnail } from '@bb/domain/dist/types';
import { find } from 'lodash';
import { AuthContext } from 'contexts';

import { ESTourItemResponse } from '../types';
import { buildItemCountText } from '../../TourEditPage/utils';

interface DashboardTourProps {
  tour: Record<
    string | 'firstItem', // AppointmentTourItemId | 'firstItem'
    (ESTourItemResponse | AppointmentWithThumbnail) & {
      isSupplementary?: boolean;
    }
  >;
  showFullTour?: boolean;
  simplifiedLayout?: boolean;
  fromTourPage?: boolean;
}

const formatTime = createFormatDateFunction('h:mmaaa');

export const isItemVisible = (
  item: (ESTourItemResponse | AppointmentWithThumbnail) & {
    isSupplementary?: boolean;
  },
  showFullTour?: boolean,
) =>
  (!item.isSupplementary || showFullTour) &&
  (item.type === 'APPOINTMENT' || item.docType === 'appointment');

const DashboardTour: React.FC<DashboardTourProps> = ({
  fromTourPage,
  tour,
  showFullTour,
  simplifiedLayout,
}) => {
  const { auth } = useContext(AuthContext);
  const [tourItemsOrderedArray, setTourItemsOrderedArray] = useState<
    (typeof tour)[number][]
  >([]);
  const [startTime, setStartTime] = useState<string | undefined>();
  const [endTime, setEndTime] = useState<string | undefined>();
  const [tourItemCount, setTourItemCount] = useState<number>(0);

  useEffect(() => {
    const tourItemsOrderedArrayTemp = [];
    let currentNode: string | undefined | null = 'firstItem';
    let startTimeTemp: string | undefined;
    let endTimeTemp: string | undefined;
    try {
      let itemCount = 0;
      while (currentNode) {
        itemCount += 1;
        // Avoids displaying ROUTE_STOP type and skip supplementary if user is performing a search
        if (isItemVisible(tour[currentNode], showFullTour)) {
          tourItemsOrderedArrayTemp.push(tour[currentNode]);
        }

        // finding the start time of the tour based on start of the earliest appointment
        const timeUsed = ['time_change', 'suggested_time_change'].includes(
          tour[currentNode].status || '',
        )
          ? tour[currentNode].originalTime
          : tour[currentNode].time;
        const timeEndUsed = ['time_change', 'suggested_time_change'].includes(
          tour[currentNode].status || '',
        )
          ? tour[currentNode].originalTimeEnd
          : tour[currentNode].timeEnd;
        if (timeUsed) {
          if (startTimeTemp) {
            startTimeTemp = isBefore(
              new Date(timeUsed),
              new Date(startTimeTemp),
            )
              ? timeUsed
              : startTimeTemp;
          } else {
            startTimeTemp = timeUsed;
          }
        }

        // finding the end time of the tour based on end of the last appointment
        if (timeEndUsed) {
          if (endTimeTemp) {
            endTimeTemp = isAfter(new Date(timeEndUsed), new Date(endTimeTemp))
              ? timeEndUsed
              : endTimeTemp;
          } else {
            endTimeTemp = timeEndUsed;
          }
        }

        currentNode = tour[currentNode].nextItem;
      }

      setTourItemCount(itemCount);

      if (itemCount < Object.keys(tour).length) {
        throw new Error(
          `The tour data is corrupted tourId: ${
            find(tour, 'appointmentTourId')?.appointmentTourId
          }`,
        );
      }
    } catch (err) {
      errorReporter.error(
        `Error trying to load the tour ${
          find(tour, 'appointmentTourId')?.appointmentTourId
        }: ${err}`,
      );
      message.error(`Error loading tour "${find(tour, 'tourName')?.tourName}"`);
    }

    setTourItemsOrderedArray(tourItemsOrderedArrayTemp);
    setStartTime(startTimeTemp);
    setEndTime(endTimeTemp);
  }, [showFullTour, tour]);
  if (!tour.firstItem) return <></>;

  return (
    <>
      <StyledTourHeader $simplifiedLayout={simplifiedLayout}>
        <StyledTourTitleContainer>
          <TourTitle
            data-cy="tour-title"
            level={5}
            $simplifiedLayout={simplifiedLayout}
          >
            {tour.firstItem.tourName}
          </TourTitle>
          <TimeText
            data-cy="tour-time"
            type="secondary"
            nowrap
            ellipsis={simplifiedLayout ? 200 : 150}
            $simplifiedLayout={simplifiedLayout}
          >
            {startTime && endTime
              ? `${formatTime(
                  startTime,
                  getLocalTimeZone(auth),
                )} - ${formatTime(endTime, getLocalTimeZone(auth))} `
              : `No times selected `}
            • {buildItemCountText(tourItemCount)}
          </TimeText>
        </StyledTourTitleContainer>
        {!fromTourPage &&
          (simplifiedLayout ? (
            <Link
              data-cy="tour-redirect-link"
              to={`/tours/${tour.firstItem.appointmentTourId}`}
            >
              Edit tour
            </Link>
          ) : (
            <Link
              data-cy="tour-redirect-link"
              to={`/tours/${tour.firstItem.appointmentTourId}`}
            >
              <Button
                icon={
                  <Icon type="antdEditFilled" width={12} offset={[0, -3]} />
                }
              >
                Edit
              </Button>
            </Link>
          ))}
      </StyledTourHeader>
      {tourItemsOrderedArray.map((appointment, apptIndex) => (
        <AppointmentListItem
          key={`appt-item-${apptIndex}`}
          appointment={appointment}
          isBuyer
          isLastItem={apptIndex === tourItemsOrderedArray.length - 1}
        />
      ))}
    </>
  );
};

export default DashboardTour;

const StyledTourHeader = styled.div<{ $simplifiedLayout?: boolean }>`
  display: flex;
  flex-direction: ${({ $simplifiedLayout }) =>
    $simplifiedLayout ? 'column' : 'row'};
  align-items: ${({ $simplifiedLayout }) =>
    $simplifiedLayout ? 'flex-start' : 'center'};
  justify-content: space-between;
`;

const StyledTourTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 1.2rem;
`;

const TourTitle = styled(Title)<{ $simplifiedLayout?: boolean }>`
  ${({ $simplifiedLayout }) =>
    $simplifiedLayout
      ? `font-size: 14px !important;`
      : 'font-size: 16px !important;'}
  margin-bottom: 2px !important;
`;

const TimeText = styled(Text)<{ $simplifiedLayout?: boolean }>`
  ${({ $simplifiedLayout }) =>
    $simplifiedLayout ? 'font-size: 14px;' : 'font-size: 11px;'}
`;
